"use client"

import { useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Badge, Button, Spinner, Alert } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { ArrowLeft, CheckCircle, XCircle } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { getTicketById, updateTicketStatus, clearCurrentTicket } from "../../redux/slices/ticketsSlice"
import { fetchBuildings } from "../../redux/slices/buildingsSlice"
import { formatDate } from "../../utils/timestamp"
import { useAuth } from "../../contexts/auth-context"

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { currentTicket, isLoading, error } = useAppSelector((state) => state.tickets)
  const { user } = useAuth()
  const { buildings } = useAppSelector((state) => state.buildings)
  const isAdmin = user?.role === "admin"

  useEffect(() => {
    dispatch(fetchBuildings(false))
  }, [dispatch])

  useEffect(() => {
    if (ticketId) {
      dispatch(getTicketById(ticketId))
    }

    return () => {
      dispatch(clearCurrentTicket())
    }
  }, [dispatch, ticketId])

  const handleStatusChange = async (status: "open" | "closed") => {
    if (!ticketId) return

    try {
      await dispatch(updateTicketStatus({ ticketId, status })).unwrap()
    } catch (error) {
      console.error("Failed to update status:", error)
    }
  }

  const handleBackToTickets = () => {
    if (isAdmin) {
      // Check if we came from admin tickets with filters
      const referrer = location.state?.from
      if (referrer && referrer.includes("/dashboard/admin/tickets")) {
        navigate(referrer)
      } else {
        navigate("/dashboard/admin/tickets")
      }
    } else {
      navigate("/dashboard/tickets")
    }
  }

  const getBuildingName = (buildingId: string) => {
    const building = buildings.find((b) => b.id === buildingId)
    return building ? building.name : buildingId
  }

  const getLastMessage = () => {
    if (!currentTicket?.comments || currentTicket.comments.length === 0) {
      return null
    }

    const lastComment = currentTicket.comments[currentTicket.comments.length - 1]
    return lastComment
  }

  if (isLoading && !currentTicket) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        {error}
      </Alert>
    )
  }

  if (!currentTicket) {
    return (
      <Alert variant="warning" className="my-3">
        {t("ticketNotFound")}
      </Alert>
    )
  }

  const lastMessage = getLastMessage()

  return (
    <>
      {/* Mobile Layout */}
      <div className="d-md-none" style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <div
          className="p-3 d-flex align-items-center gap-3"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1030,
            height: "70px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <button className="btn-back-circle" onClick={handleBackToTickets}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="fs-5 fw-bold mb-0 text-white">{t("ticketDetails")}</h1>
          {isAdmin && (
            <div className="ms-auto">
              {currentTicket.status === "open" ? (
                <Button className="btn-white-pill" size="sm" onClick={() => handleStatusChange("closed")}>
                  <XCircle size={16} className="me-1" />
                  {t("close")}
                </Button>
              ) : (
                <Button className="btn-white-pill" size="sm" onClick={() => handleStatusChange("open")}>
                  <CheckCircle size={16} className="me-1" />
                  {t("reopen")}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex-grow-1 overflow-auto px-2 px-md-4" style={{}}>
          <div className="d-flex flex-column gap-4 pt-3">
            {/* Latest message card */}
            <div
              className="p-4 rounded-4"
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              {lastMessage ? (
                <>
                  <div className="d-flex justify-content-start mb-3">
                    <div
                      className="p-3 rounded-3 position-relative text-white"
                      style={{
                        background: "rgba(255, 255, 255, 0.15)",
                        maxWidth: "80%",
                        wordWrap: "break-word",
                      }}
                    >
                      <div className="small opacity-75 mb-1">
                        {lastMessage.userFullName} • {formatDate(lastMessage.createdAt)}
                      </div>
                      <div style={{ whiteSpace: "pre-wrap" }}>{lastMessage.content}</div>
                    </div>
                  </div>
                  <hr className="border-white opacity-25 my-3" />
                </>
              ) : (
                <>
                  <div className="text-center text-white opacity-75 mb-3">{t("noCommentsYet")}</div>
                  <hr className="border-white opacity-25 my-3" />
                </>
              )}
              <div className="text-center">
                <Button
                  className="btn-white-pill-bold"
                  onClick={() => navigate(`/dashboard/tickets/${ticketId}/thread`)}
                >
                  {t("showFullConversation")}
                </Button>
              </div>
            </div>

            {/* Ticket details card */}
            <div
              className="p-4 rounded-4"
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className={`rounded-circle mt-1 ${currentTicket.status === "open" ? "bg-warning" : "bg-success"}`}
                    style={{ width: "12px", height: "12px", minWidth: "12px" }}
                  ></div>
                  <div>
                    <h2 className="fs-4 mb-2 text-white fw-normal">{currentTicket.title}</h2>
                    <div className="d-flex gap-2 mb-3">
                      <Badge bg="primary">{t(currentTicket.type)}</Badge>
                      <Badge bg={currentTicket.status === "open" ? "warning" : "success"}>
                        {t(currentTicket.status)}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="text-white opacity-75 text-end">
                  <div className="fw-medium">{formatDate(currentTicket.createdAt).split(" ")[1]}</div>
                  <div className="small">{formatDate(currentTicket.createdAt).split(" ")[0]}</div>
                </div>
              </div>

              <div className="mb-3 text-white">
                <div className="d-flex justify-content-between mb-2">
                  <div>
                    <strong>{t("submittedBy")}:</strong> {currentTicket.userFullName}
                  </div>
                </div>
                <div className="d-flex gap-3">
                  <div>
                    <strong>{t("building")}:</strong> {getBuildingName(currentTicket.building)}
                  </div>
                  <div>
                    <strong>{t("room")}:</strong> {currentTicket.roomNumber}
                  </div>
                </div>
              </div>

              <div className="mb-3 text-white">
                <h3 className="fs-5 mb-2 opacity-75">{t("description")}</h3>
                <p style={{ whiteSpace: "pre-wrap" }}>{currentTicket.description}</p>
              </div>

              {currentTicket.hasImage && (
                <div className="mb-3">
                  <h3 className="fs-5 mb-2 text-white opacity-75">{t("attachment")}</h3>
                  <img
                    src={currentTicket.imageUrl || "/placeholder.svg"}
                    alt="Ticket attachment"
                    className="img-fluid rounded-3"
                    style={{ maxHeight: "300px" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="d-none d-md-flex flex-column py-4 px-2 px-md-4" style={{ height: "calc(100vh - 8rem)" }}>
        <div className="d-flex align-items-center gap-3 mb-4">
          <button className="btn-back-circle" onClick={handleBackToTickets}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="fs-2 fw-bold mb-0 text-white">{t("ticketDetails")}</h1>
        </div>

        <div className="d-flex flex-column gap-4 flex-grow-1" style={{ minHeight: 0, overflowY: "auto" }}>
          {/* Latest message card */}
          <div
            className="p-4 rounded-4"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            {lastMessage ? (
              <>
                <div className="d-flex justify-content-start mb-3">
                  <div
                    className="p-3 rounded-3 position-relative text-white"
                    style={{
                      background: "rgba(255, 255, 255, 0.15)",
                      maxWidth: "80%",
                      wordWrap: "break-word",
                    }}
                  >
                    <div className="small opacity-75 mb-1">
                      {lastMessage.userFullName} • {formatDate(lastMessage.createdAt)}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{lastMessage.content}</div>
                  </div>
                </div>
                <hr className="border-white opacity-25 my-3" />
              </>
            ) : (
              <>
                <div className="text-center text-white opacity-75 mb-3">{t("noCommentsYet")}</div>
                <hr className="border-white opacity-25 my-3" />
              </>
            )}
            <div className="text-center">
              <Button className="btn-white-pill-bold" onClick={() => navigate(`/dashboard/tickets/${ticketId}/thread`)}>
                {t("showFullConversation")}
              </Button>
            </div>
          </div>

          {/* Ticket details card */}
          <div
            className="p-4 rounded-4"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div className="d-flex align-items-start gap-3">
                <div
                  className={`rounded-circle mt-1 ${currentTicket.status === "open" ? "bg-warning" : "bg-success"}`}
                  style={{ width: "12px", height: "12px", minWidth: "12px" }}
                ></div>
                <div>
                  <h2 className="fs-3 mb-2 text-white fw-normal">{currentTicket.title}</h2>
                  <div className="d-flex gap-2">
                    <Badge bg="primary">{t(currentTicket.type)}</Badge>
                    <Badge bg={currentTicket.status === "open" ? "warning" : "success"}>
                      {t(currentTicket.status)}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column align-items-end gap-2">
                <div className="text-white opacity-75 text-end">
                  <div className="fw-medium">{formatDate(currentTicket.createdAt).split(" ")[1]}</div>
                  <div className="small">{formatDate(currentTicket.createdAt).split(" ")[0]}</div>
                </div>
                {isAdmin && (
                  <div>
                    {currentTicket.status === "open" ? (
                      <Button className="btn-white-pill" size="sm" onClick={() => handleStatusChange("closed")}>
                        <XCircle size={16} className="me-1" />
                        {t("closeTicket")}
                      </Button>
                    ) : (
                      <Button className="btn-white-pill" size="sm" onClick={() => handleStatusChange("open")}>
                        <CheckCircle size={16} className="me-1" />
                        {t("reopenTicket")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4 text-white">
              <div className="d-flex justify-content-between mb-2">
                <div>
                  <strong>{t("submittedBy")}:</strong> {currentTicket.userFullName}
                </div>
              </div>
              <div className="d-flex gap-4">
                <div>
                  <strong>{t("building")}:</strong> {getBuildingName(currentTicket.building)}
                </div>
                <div>
                  <strong>{t("room")}:</strong> {currentTicket.roomNumber}
                </div>
              </div>
            </div>

            <div className="mb-4 text-white">
              <h3 className="fs-5 mb-2 opacity-75">{t("description")}</h3>
              <p style={{ whiteSpace: "pre-wrap" }}>{currentTicket.description}</p>
            </div>

            {currentTicket.hasImage && (
              <div className="mb-3">
                <h3 className="fs-5 mb-2 text-white opacity-75">{t("attachment")}</h3>
                <img
                  src={currentTicket.imageUrl || "/placeholder.svg"}
                  alt="Ticket attachment"
                  className="img-fluid rounded-3"
                  style={{ maxHeight: "300px" }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
